<script setup>
import { numberWithCommas } from '@/plugins/misc'
import { escapeHtml } from '@/utils/agentMessageHtml'

defineProps({
  pricingBreakdown: { type: Object, default: null },
  firstRestaurantName: { type: String, default: '' },
})
</script>

<template>
  <details class="payment-info-section">
    <summary
      class="payment-info-header"
      style="cursor: pointer; list-style: none;"
    >
      <VIcon
        icon="mdi-shield-check-outline"
        size="22"
        color="primary-dark"
      />
      <!-- Kapil 2026-09-30 (RM-360): one name for this section, in the app and on /request-details-faqs -->
      <span class="payment-info-title">How guaranteed F&amp;B spend works</span>
      <span style="margin-left: auto; font-size: 0.7rem; color: #666;">▸</span>
    </summary>

    <div
      class="payment-info-body"
      style="margin-top: 12px;"
    >
      <template v-if="pricingBreakdown">
        <div class="payment-info-main">
          <div
            class="payment-info-desc"
            style="font-size: 16px;"
          >
            <span class="payment-info-amount">${{ numberWithCommas(pricingBreakdown.total) }}</span>
            {{ $t('reservation.payment.holdDescription') }}
          </div>
        </div>

        <p
          class="payment-info-note"
          v-html="$t('reservation.payment.holdReleaseInfo', { restaurant: `<strong>${escapeHtml(firstRestaurantName)}</strong>`, amount: `$${numberWithCommas(pricingBreakdown.baseSpend)}` })"
        />

        <details class="payment-info-details">
          <summary>{{ $t('reservation.payment.whatIfBelowMin') }}</summary>
          <div class="payment-info-details-content">
            <p>{{ $t('reservation.payment.restaurantMayEither', { restaurant: firstRestaurantName }) }}</p>
            <ul>
              <li>{{ $t('reservation.payment.chargeFlatFee', { amount: `$${numberWithCommas(pricingBreakdown.baseSpend)}` }) }}</li>
              <li>{{ $t('reservation.payment.receiveRemainder') }}</li>
            </ul>
            <p>RM takes no commission or fees</p>
          </div>
        </details>
      </template>
      <div
        v-else
        class="payment-info-desc"
        style="font-size: 16px;"
      >
        Select a guaranteed spend above to see your card-hold amount.
      </div>
    </div>
  </details>
</template>

<style lang="scss" scoped>
$primary-teal: var(--ds-brand-primary-dark);
$text-dark: var(--ds-text-dark);
$text-medium: var(--ds-text-medium);
$border-light: var(--ds-border-light);
$bg-white: var(--ds-surface-card);

.payment-info-section {
  background: $bg-white;
  border: 1px solid $border-light;
  border-radius: 12px;
  padding: 20px;
}

.payment-info-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.payment-info-title {
  font-size: 16px;
  font-weight: 600;
  color: $text-dark;
}

.payment-info-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.payment-info-amount {
  font-size: 16px;
  font-weight: 700;
  color: $primary-teal;
}

.payment-info-desc {
  font-size: 16px;
  color: $text-medium;
}

.payment-info-note {
  font-size: 16px;
  color: #555;
  line-height: 1.5;
  margin: 0 0 12px 0;

  strong {
    font-weight: 600;
  }
}

.payment-info-details {
  summary {
    font-size: 16px;
    color: $primary-teal;
    cursor: pointer;
    font-weight: 500;
    list-style: none;
    display: flex;
    align-items: center;
    gap: 4px;

    &::-webkit-details-marker {
      display: none;
    }

    &::before {
      content: '▸';
      font-size: 0.7rem;
      transition: transform 0.2s ease;
    }

    &:hover {
      text-decoration: underline;
    }
  }

  &[open] summary::before {
    transform: rotate(90deg);
  }
}

.payment-info-details-content {
  margin-top: 10px;
  padding-left: 12px;
  border-left: 2px solid #e0ebe9;

  p {
    font-size: 16px;
    color: $text-medium;
    margin: 0 0 6px 0;
  }

  ul {
    margin: 0 0 0 16px;
    padding: 0;
    font-size: 16px;
    color: $text-medium;
    list-style-type: disc;

    li {
      margin-bottom: 4px;
      line-height: 1.4;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

details.payment-info-section {
  summary::-webkit-details-marker {
    display: none;
  }

  &[open] > summary span:last-child {
    transform: rotate(90deg);
    display: inline-block;
  }
}
</style>
